import React, { useEffect, useRef, useState } from 'react';
import { suggestAddresses } from '../services/intelligenceApi';

/**
 * Free OpenStreetMap address typeahead (Nominatim/Photon via /intelligence/address-suggest).
 * Always searches the full input string; ignores stale responses from earlier keystrokes.
 */
function AddressSuggestInput({
  value = '',
  onChange,
  onSelect,
  disabled = false,
  placeholder = 'Start typing a street address…',
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const wrapRef = useRef(null);
  const requestSeq = useRef(0);

  useEffect(() => {
    const onDoc = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    const q = (value || '').trim();
    if (disabled || q.length < 3) {
      setResults([]);
      setLoading(false);
      return undefined;
    }

    const seq = ++requestSeq.current;
    setLoading(true);
    // Longer debounce: Nominatim wants ~1 req/s and we want the full phrase, not every keystroke.
    const timer = setTimeout(async () => {
      try {
        const res = await suggestAddresses(q, { limit: 8 });
        if (seq !== requestSeq.current) return;
        setResults(res.data.results || []);
        setOpen(true);
      } catch {
        if (seq !== requestSeq.current) return;
        setResults([]);
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    }, 450);

    return () => clearTimeout(timer);
  }, [value, disabled]);

  return (
    <div className="address-suggest" ref={wrapRef}>
      <input
        className="form-input"
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete="off"
        onFocus={() => {
          if (results.length) setOpen(true);
        }}
        onChange={(e) => {
          onChange?.(e.target.value);
          onSelect?.(null);
        }}
      />
      {open && (loading || results.length > 0 || (value || '').trim().length >= 3) && (
        <div className="address-suggest-menu">
          {loading && <div className="entity-picker-empty">Searching full address…</div>}
          {!loading && results.length === 0 && (
            <div className="entity-picker-empty">No matches — keep typing city/state or enter the address manually.</div>
          )}
          {!loading && results.map((item) => (
            <button
              key={`${item.source}-${item.osm_type || 'x'}-${item.osm_id || item.label}-${item.lat}-${item.lon}`}
              type="button"
              className="entity-picker-option"
              onClick={() => {
                onChange?.(item.label);
                onSelect?.(item);
                setOpen(false);
              }}
            >
              <div className="entity-picker-option-text">
                <strong>{item.label}</strong>
                {(item.city || item.country) && (
                  <span>{[item.city, item.state, item.country].filter(Boolean).join(' · ')}</span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default AddressSuggestInput;
